<!-- Generic format_specific_data form: one heading per x-ui group, fields in x-ui order, a nested
     object as a fieldset of its own fields. Nothing here names a template or a key; everything
     comes from the schema (see fsd-schema.ts). An empty schema renders nothing. -->
@if (!empty) {
  @if (ruleBefore) {
    <hr class="cl-spacing-medium">
  }
  <!-- Keyed by the schema revision: a new template re-creates the groups. A block is one group, or a
       `sections` heading (section-headings.ts) over its member groups; rulers only between
       blocks. Groups are div role="group", not <section>: castlabs-ui pads every section, which
       stacked into large gaps under the rulers. -->
  @for (rev of [schemaRev]; track rev) {
  @for (block of blocks; track block.key) {
    @if (block.ruled) {
      <hr class="cl-spacing-medium">
    }
    @if (block.title) {
      <div role="group" class="cl-spacing-medium" [attr.data-section]="block.title" [attr.aria-labelledby]="idPrefix + '-' + block.key">
        <h3 [id]="idPrefix + '-' + block.key">{{ block.title }}</h3>
        <!-- One column per member group: its lead control on top, its details right under it. -->
        <div class="row">
          @for (group of block.columns; track group.name) {
            <div class="col-12 col-lg-6" [attr.data-group]="group.name">
              <ng-container *ngTemplateOutlet="groupFields; context: {group: group}"></ng-container>
            </div>
          }
        </div>
      </div>
    } @else {
      <ng-container *ngTemplateOutlet="groupBlock; context: {group: block.groups[0]}"></ng-container>
    }
  }
  }

  <!-- One standalone group under its h3. -->
  <ng-template #groupBlock let-group="group">
    <div role="group" class="cl-spacing-medium" [attr.data-group]="group.name" [attr.aria-labelledby]="groupId(group)">
      <h3 [id]="groupId(group)">{{ group.name || 'Template settings' }}</h3>
      <ng-container *ngTemplateOutlet="groupFields; context: {group: group}"></ng-container>
    </div>
  </ng-template>

  <ng-template #groupFields let-group="group">
    @if (hasPlain(group)) {
      <ng-container *ngTemplateOutlet="section; context: {group: group, advanced: false}"></ng-container>
    }
    <!-- The group's x-ui.advanced fields: always shown, after its other fields, in a part of their
         own labelled "Advanced" (user decision on #2761: no switch, no accordion). -->
    @if (hasGroupAdvanced(group)) {
      <!-- Set apart by its legend and medium spacing, no ruler (styleguide: fieldset + legend). -->
      <fieldset class="cl-spacing-medium" data-advanced-fields>
        <legend>Advanced</legend>
        <ng-container *ngTemplateOutlet="section; context: {group: group, advanced: true}"></ng-container>
      </fieldset>
    }
  </ng-template>

  <!-- The fields of one group that belong to the plain or to the advanced part. A nested object is
       one fieldset, in the plain part when it has plain fields (its advanced ones in an "Advanced" part
       inside it, never a second fieldset of the same name), else in the advanced part. Its legend names a group,
       so no required asterisk. -->
  <ng-template #section let-group="group" let-advanced="advanced">
    <div class="row">
      @for (f of group.fields; track f.path) {
        @if (f.widget === 'object') {
          @if (advanced ? !childrenIn(f, false).length && childrenIn(f, true).length : childrenIn(f, false).length) {
            <div class="col-12" [attr.data-field]="f.path">
              <!-- No spacing class: measured on :4200 the column already puts 16 px above it, as above a label. -->
              <fieldset>
                <legend>{{ f.label }}</legend>
                <div class="row">
                  @for (c of childrenIn(f, advanced); track c.path) {
                    <ng-container *ngTemplateOutlet="field; context: {$implicit: c}"></ng-container>
                  }
                </div>
                @if (!advanced && childrenIn(f, true).length) {
                  <fieldset class="cl-spacing-medium" data-advanced-fields>
                    <legend>Advanced</legend>
                    <div class="row">
                      @for (c of childrenIn(f, true); track c.path) {
                        <ng-container *ngTemplateOutlet="field; context: {$implicit: c}"></ng-container>
                      }
                    </div>
                  </fieldset>
                }
                @if (f.help && !advanced) {
                  <p class="valid-feedback">{{ f.help }}</p>
                }
              </fieldset>
            </div>
          }
        } @else if (f.advanced === advanced && !absorbed.has(f.path)) {
          <ng-container *ngTemplateOutlet="field; context: {$implicit: f}"></ng-container>
        }
      }
    </div>
  </ng-template>
  @if (errors['']) {
    @for (e of errors['']; track e) {
      <p class="invalid-feedback d-block">{{ e }}</p>
    }
  }
}

<ng-template #field let-f>
  <!-- A select whose option texts are long spans the full width, so they are not cut off. -->
  <div [class]="fieldClass(f)"
       [attr.data-field]="f.path">
    @switch (f.widget) {
      @case ('ladder') {
        <app-encoding-ladder [formControl]="ladderControl(f)"></app-encoding-ladder>
      }
      @case ('switch') {
        <!-- cl-spacing-none: the column already carries the field spacing; castlabs-ui's 16 px above
             a form-check put a switch twice as far from a legend as a label (measured on :4200). -->
        <div class="form-check cl-spacing-none">
          <input class="form-check-input" type="checkbox" [id]="fieldId(f)" [disabled]="disabled || f.readOnly"
                 [class.invalid]="showErrors(f)"
                 [ngModel]="!!val(f.path)" (ngModelChange)="set(f.path, $event)"
                 [ngModelOptions]="{standalone: true}">
          <label class="form-check-label" [for]="fieldId(f)">{{ f.label }}</label>
        </div>
      }
      @case ('multiselect') {
        <!-- cl-spacing-small: 16 px above the legend, as above a label (measured on :4200: 0 without). -->
        <fieldset class="cl-spacing-small">
          <legend>{{ f.label }}@if (f.required) {<span class="required">*</span>}</legend>
          <!-- The options, and in a section column the switch right after the list as their last
               sibling, right-aligned (DRM systems ... DRMtoday Onboard). -->
          <div class="d-flex flex-wrap align-items-center" data-options>
            <!-- cl-spacing-none: castlabs-ui gives the first check 12 px above, the others 4 px, which set
                 the first one lower in this flex row (seen on :4200). -->
            @for (o of f.options; track $index) {
              <div class="form-check form-check-inline cl-spacing-none">
                <input class="form-check-input" type="checkbox" [id]="fieldId(f) + '-' + $index"
                       [disabled]="disabled || f.readOnly" [checked]="isSelected(f, o.value)"
                       [class.invalid]="showErrors(f)" [attr.aria-invalid]="showErrors(f) || null"
                       (change)="toggleOption(f, o.value, $any($event.target).checked)">
                <label class="form-check-label" [for]="fieldId(f) + '-' + $index">{{ o.label }}</label>
              </div>
            }
            @if (trailing.get(f.path); as t) {
              <!-- A spacer, not ms-auto: it pushes the switch right while the line fits, and when the
                   switch wraps it stays behind on the first line, so the switch starts the next line
                   on the left (user decision on #2761). -->
              <span class="flex-grow-1" aria-hidden="true" data-spacer></span>
              <div class="form-check form-check-inline cl-spacing-none" [attr.data-field]="t.path">
                <!-- Marked and reported like a field of its own, so an invalid value (e.g. an imported
                     "true" string) is visible and a refused submit can focus it (Percival). -->
                <input class="form-check-input" type="checkbox" [id]="fieldId(t)" [disabled]="disabled || t.readOnly"
                       [class.invalid]="showErrors(t)" [attr.aria-invalid]="showErrors(t) || null"
                       [ngModel]="!!val(t.path)" (ngModelChange)="set(t.path, $event)"
                       [ngModelOptions]="{standalone: true}">
                <label class="form-check-label" [for]="fieldId(t)">{{ t.label }}</label>
              </div>
            }
          </div>
          @if (trailing.get(f.path); as t) {
            @if (showErrors(t)) {
              <p class="invalid-feedback d-block" [attr.data-feedback-for]="t.path">{{ t.label }}: {{ errors[t.path].join(' ') }}</p>
            }
          }
        </fieldset>
      }
      @default {
        <label class="form-label" [for]="fieldId(f)">{{ f.label }}@if (f.required) {<span class="required">*</span>}</label>
        @switch (f.widget) {
          @case ('select') {
            <select class="form-select" [id]="fieldId(f)" [disabled]="disabled || f.readOnly"
                    [class.invalid]="showErrors(f)"
                    [ngModel]="val(f.path)" (ngModelChange)="set(f.path, $event)"
                    [ngModelOptions]="{standalone: true}">
              @for (o of selectOptions(f); track $index) {
                <option [ngValue]="o.value">{{ o.label }}</option>
              }
            </select>
          }
          @case ('textarea') {
            <textarea class="form-control" rows="4" [id]="fieldId(f)" [disabled]="disabled || f.readOnly"
                      [class.invalid]="showErrors(f)" [attr.maxlength]="f.maxLength"
                      [ngModel]="val(f.path)" (ngModelChange)="set(f.path, $event)"
                      [ngModelOptions]="{standalone: true}"></textarea>
          }
          @case ('tags') {
            <input class="form-control" type="text" [id]="fieldId(f)" [disabled]="disabled || f.readOnly"
                   [class.invalid]="showErrors(f)"
                   [ngModel]="tagsText(f)" (change)="setTags(f, $any($event.target).value)"
                   [ngModelOptions]="{standalone: true}">
          }
          @case ('number') {
            <input class="form-control" type="number" [id]="fieldId(f)" [disabled]="disabled || f.readOnly"
                   [class.invalid]="showErrors(f)" [attr.min]="f.minimum" [attr.max]="f.maximum"
                   [attr.step]="f.integer ? 1 : 'any'"
                   [ngModel]="val(f.path)" (ngModelChange)="set(f.path, $event)"
                   [ngModelOptions]="{standalone: true}">
          }
          @case ('day') {
            <!-- A date-time offered as a day (x-ui.day): shows the value's UTC day; picking one writes
                 its first or last second UTC, an untouched value stays as given. -->
            <input class="form-control" type="date" [id]="fieldId(f)" [disabled]="disabled || f.readOnly"
                   [class.invalid]="showErrors(f)"
                   [ngModel]="utcDayOf(val(f.path))" (ngModelChange)="set(f.path, dayToUtc($event, f.xui.day))"
                   [ngModelOptions]="{standalone: true}">
          }
          @default {
            <input class="form-control"
                   [type]="f.widget === 'date' ? 'date' : f.widget === 'datetime' ? 'datetime-local' : 'text'"
                   [id]="fieldId(f)" [disabled]="disabled || f.readOnly" [class.invalid]="showErrors(f)"
                   [attr.step]="f.widget === 'datetime' ? 1 : null"
                   [ngModel]="val(f.path)" (ngModelChange)="set(f.path, $event)"
                   [ngModelOptions]="{standalone: true}">
          }
        }
      }
    }
    <!-- No empty help placeholder (valid-feedback-none): it added an empty line under every field
         without help. castlabs-ui shows .invalid-feedback after an input.invalid by itself; a widget that wraps its
         input (switch, multiselect, ladder) needs it shown explicitly. One line: castlabs-ui only
         reaches the first siblings after the input. -->
    @if (showErrors(f)) {
      <p class="invalid-feedback" [class.d-block]="!inputPrecedesFeedback(f)">{{ errors[f.path].join(' ') }}</p>
    } @else if (f.help) {
      <p class="valid-feedback">{{ f.help }}</p>
    }
  </div>
</ng-template>
